/**
 * Every public page: its address, component, and what search engines and AI answer
 * engines read about it (title, description, language twin, structured data).
 * The browser router, the build-time prerender and the sitemap all read this list.
 */
import type { ComponentType } from 'react';
import { BEAT_FAQ, BeatMaker, GROUP_FAQ, GroupActivity, HE_FAQ, HOST_FAQ, HOST_STEPS, HowToHost, LandingHe, NotFound, PARTY_FAQ, PartyGame, type Faq } from './Guides.tsx';
import { Credits, Landing, Privacy, Terms } from './Site.tsx';

export const SITE = 'https://combinator.club';
/** When the guides went up, and when their text last changed (update it with the copy). */
const GUIDES_PUBLISHED = '2026-11-03';
const GUIDES_UPDATED = '2026-11-03';

export interface PublicPage {
  path: string;
  Component: ComponentType;
  title: string;
  description: string;
  lang: 'en' | 'he';
  /** The same page in the other language (hreflang). */
  twin?: string;
  /** Sitemap priority; 0 = not in the sitemap. */
  priority: number;
  /** Structured data (JSON-LD) for this page. */
  ld: object[];
}

/** The landing FAQ, as shown on the page (Site.tsx). */
const HOME_FAQ: Faq = [
  { q: 'Does everyone need an account?', a: 'No. Only the person who opens a jam signs in, with a code we email them (no password). Phones join with the QR code.' },
  { q: 'What do I need?', a: 'A laptop (Chrome, Edge or Safari) with speakers, and phones. Wired or HDMI speakers feel tighter than Bluetooth.' },
  { q: 'Do the phones have to be on the same Wi-Fi?', a: "It's best, but not required: phones on mobile data can join too. Same Wi-Fi gives the lowest delay." },
  { q: 'Can I keep what we made?', a: 'Yes. Songs save to your account, you can download the song file, and "Record audio" exports the audio (the last 5 minutes are always kept, too).' },
];

const faqLd = (items: Faq) => ({
  '@type': 'FAQPage',
  mainEntity: items.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
});

const ORG = { '@type': 'Organization', '@id': `${SITE}/#org`, name: 'Combinator', url: `${SITE}/`, logo: `${SITE}/logo.png` };

const app = (lang: 'en' | 'he') => ({
  '@type': 'WebApplication',
  '@id': `${SITE}/#app`,
  name: 'Combinator',
  url: `${SITE}/`,
  inLanguage: lang,
  description:
    lang === 'he'
      ? 'ג׳אם חינם בדפדפן: מחשב או טלוויזיה מנגנים את המוזיקה, וכל טלפון בחדר הופך לתופים, בס, קלידים או אפקטים. מצטרפים בסריקת QR, בלי אפליקציה ובלי הרשמה.'
      : 'A free jam session in the browser: one laptop or TV plays the music and every phone in the room becomes an instrument (drums, bass, keys, sampler, effects). Players join by scanning a QR code, with no app and no account.',
  applicationCategory: 'MultimediaApplication',
  applicationSubCategory: 'Music sequencer',
  operatingSystem: 'Any (web browser)',
  browserRequirements: 'Requires a modern browser such as Chrome, Edge or Safari',
  isAccessibleForFree: true,
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
  publisher: { '@id': `${SITE}/#org` },
  featureList: [
    'Phones as drums, bass, keys, sampler and effects',
    'Join by QR code, no app',
    'Easy mode: hits on the beat, notes in key',
    'Pattern sequencer, piano roll and song timeline',
    'Mixer with EQ and compressor',
    'MIDI keyboard input on the host',
    'Record and export the jam',
  ],
});

const crumbs = (name: string, path: string) => ({
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Combinator', item: `${SITE}/` },
    { '@type': 'ListItem', position: 2, name, item: `${SITE}${path}` },
  ],
});

const guide = (path: string, Component: ComponentType, title: string, h1: string, description: string, faq: Faq, extra: object[] = []): PublicPage => ({
  path,
  Component,
  title,
  description,
  lang: 'en',
  priority: 0.8,
  ld: [{ '@type': 'Article', headline: h1, description, inLanguage: 'en', datePublished: GUIDES_PUBLISHED, dateModified: GUIDES_UPDATED, image: `${SITE}/og.jpg`, author: { '@id': `${SITE}/#org` }, publisher: { '@id': `${SITE}/#org` }, mainEntityOfPage: `${SITE}${path}` }, crumbs(h1, path), faqLd(faq), ...extra],
});

export const PAGES: PublicPage[] = [
  {
    path: '/',
    Component: Landing,
    title: 'Combinator — every phone in the room is an instrument',
    description: 'A free jam session for the room: a laptop or TV plays the music, every phone becomes drums, bass, keys or effects. No app, no account to join.',
    lang: 'en',
    twin: '/he',
    priority: 1,
    ld: [ORG, app('en'), faqLd(HOME_FAQ)],
  },
  {
    path: '/he',
    Component: LandingHe,
    title: 'Combinator — כל טלפון בחדר הופך לכלי נגינה | ג׳אם חינם בדפדפן',
    description: 'ג׳אם חינם לכל החדר: מחשב או טלוויזיה מנגנים, וכל טלפון הופך לתופים, בס, קלידים או אפקטים. בלי אפליקציה ובלי הרשמה. למסיבות, לכיתה ולגיבוש.',
    lang: 'he',
    twin: '/',
    priority: 0.9,
    ld: [ORG, app('he'), faqLd(HE_FAQ)],
  },
  guide(
    '/how-to-host',
    HowToHost,
    'How to host a phone jam session: setup, speakers, tips | Combinator',
    'How to host a jam with Combinator',
    'Set up a jam where every phone is an instrument: what you need, five steps, speakers and Wi-Fi, TVs, and quick fixes when something feels off.',
    HOST_FAQ,
    [
      {
        '@type': 'HowTo',
        name: 'How to host a jam with Combinator',
        totalTime: 'PT2M',
        supply: [{ '@type': 'HowToSupply', name: 'A laptop or TV with a browser' }, { '@type': 'HowToSupply', name: 'Speakers' }, { '@type': 'HowToSupply', name: 'Phones' }],
        step: HOST_STEPS.map((s, i) => ({ '@type': 'HowToStep', position: i + 1, name: s.name, text: s.text })),
      },
    ],
  ),
  guide(
    '/party-music-game',
    PartyGame,
    'A music game for parties: everyone plays from their phone | Combinator',
    'A music game for parties where everyone plays',
    'Turn the party into a jam: every phone becomes an instrument, one speaker plays it all, and Easy mode keeps everyone on the beat. Free, no app.',
    PARTY_FAQ,
  ),
  guide(
    '/group-music-activity',
    GroupActivity,
    'Group music activity for classes, teams and workshops | Combinator',
    'A music activity for classes, teams and workshops',
    'A music activity where the whole group plays at once on their own phones. No instruments or experience needed. Includes a 40-minute session plan.',
    GROUP_FAQ,
  ),
  guide(
    '/multiplayer-beat-maker',
    BeatMaker,
    'Multiplayer beat maker: make beats together online | Combinator',
    'A multiplayer beat maker in the browser',
    'Make beats together in real time: patterns, piano roll, song timeline, mixer and effects on the big screen, with every friend playing from their phone.',
    BEAT_FAQ,
  ),
  {
    path: '/privacy',
    Component: Privacy,
    title: 'Privacy notice | Combinator',
    description: 'What Combinator stores (only the host’s email and saved songs), why, for how long, and your rights. No ads, no analytics, no selling.',
    lang: 'en',
    priority: 0.3,
    ld: [],
  },
  {
    path: '/terms',
    Component: Terms,
    title: 'Terms of use | Combinator',
    description: 'The terms for using Combinator, the free browser jam session: your music is yours, the built-in sounds are free to use in it.',
    lang: 'en',
    priority: 0.3,
    ld: [],
  },
  {
    path: '/credits',
    Component: Credits,
    title: 'Sound credits | Combinator',
    description: 'Where every sound built into Combinator comes from, and its license.',
    lang: 'en',
    priority: 0.3,
    ld: [],
  },
];

/** The page for an unknown address (served with status 404). */
export const NOT_FOUND: PublicPage = {
  path: '/404',
  Component: NotFound,
  title: 'Page not found | Combinator',
  description: 'This page isn’t here. Start a jam, or read how Combinator works.',
  lang: 'en',
  priority: 0,
  ld: [],
};

export function pageFor(path: string): PublicPage | undefined {
  return PAGES.find((p) => p.path === path);
}
